import { S25Util } from "./s25-util";

const ALT_COMMENT_RE = /^data:\/\*(.*?)\*\//;

/** True when the string is usable as a data URL (after trim). */
export function isDataUrlString(s: string | null | undefined): boolean {
    return !!s && s.trimStart().startsWith("data:");
}

/** Reads a binary blob as a `data:*;base64,...` URL (for FileStore images not stored as text data URLs). */
export function readBlobAsDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
        reader.readAsDataURL(blob);
    });
}

/**
 * Server-enforced max file count per object ({@link com.collegenet.series25.controller.FilestoreController}).
 * This is fairly arbitrary and can be changed if/when there is demand
 */
export const FILESTORE_POOL_MAX_FILES_PER_OBJECT = 10;

export const FILESTORE_IMAGE_EXTENSIONS: readonly string[] = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

/**
 * Parsed FileStore parameters from a custom attribute value
 */
export interface FileStoreParams {
    objectType: number;
    objectId: number;
    atrbId?: number;
    fileName: string;
    /** When set, file is located by file_key as well (e.g. form submission files); objectType/objectId may be -1 */
    fileKey?: string;
}

/**
 * Resolves any image URL value into a usable <img src> URL.
 * Handles relative paths, absolute URLs, FileStore query-string URLs, and data URIs.
 */
export function resolveImageUrl(url: string | null | undefined): string | undefined {
    if (!url?.trim()) return undefined;
    if (isDataUrlString(url)) return stripAltFromDataUrl(url);
    if (url.includes("://")) return url;
    if (FileStoreUtil.isFileStoreUrl(url)) return FileStoreUtil.getImageUrl(url) ?? undefined;
    return url;
}

/** Force the browser to reload an <img src> after bytes change but the URL path/query is unchanged. */
export function appendImageSrcCacheBust(imageSrc: string): string {
    if (!imageSrc?.trim()) {
        return imageSrc ?? "";
    }
    const separator = imageSrc.includes("?") ? "&" : "?";
    return `${imageSrc}${separator}_=${Date.now()}`;
}

/** Extract embedded alt text from a data URL comment: `data:/​*alt text*​/image/png;base64,...` */
export function extractAltFromDataUrl(url: string | null | undefined): string {
    if (!url) return "";
    const m = ALT_COMMENT_RE.exec(url?.trimStart());
    return m?.[1] ?? "";
}

/** Strip the alt-text comment from a data URL so it can be used as an <img src>. */
export function stripAltFromDataUrl(url: string): string {
    return url.trimStart().replace(ALT_COMMENT_RE, "data:");
}

/** Embed alt text as a comment in a data URL: `data:/​*alt*​/image/...`. No-op if url is not a data URL or alt is empty. */
export function embedAltInDataUrl(url: string, alt: string): string {
    if (!alt?.trim() || !isDataUrlString(url)) return url;
    const clean = stripAltFromDataUrl(url);
    return `data:/*${alt.trim()}*/${clean.slice(5)}`;
}

/** Pool file reference query string without atrb_id (e.g. organization logo CA). */
export function buildObjectFilestorePoolRef(objectTypeId: number, objectId: number, fileName: string): string {
    return `object_type=${objectTypeId}&object_id=${objectId}&file_name=${encodeURIComponent(fileName)}`;
}

/**
 * FileStore utility for handling custom attribute file URLs
 * Custom attribute file URLs are stored as query strings like:
 * "object_type=2&object_id=43&atrb_id=-101&file_name=Pickles.jpg"
 */
export class FileStoreUtil {
    /** Read lazily: ProData is assigned by the host page, which can happen after this module evaluates. */
    private static get baseUrl(): string {
        return window.ProData?.webservicesBaseUrl ?? "";
    }

    static readonly IMAGE_EXTENSIONS = FILESTORE_IMAGE_EXTENSIONS;

    /**
     * Checks if a value is a FileStore custom attribute URL
     * @param value The custom attribute value to check
     * @returns true if the value contains FileStore parameters
     */
    static isFileStoreUrl(value: any): boolean {
        return (
            typeof value === "string" &&
            !!value.trim() &&
            value.includes("object_type=") &&
            value.includes("file_name=")
        );
    }

    /**
     * Parses FileStore parameters from a custom attribute value
     * @param value The custom attribute value (e.g., "object_type=2&object_id=43&atrb_id=-101&file_name=Pickles.jpg")
     * @returns Parsed parameters or null if invalid
     */
    static parseFileStoreUrl(value: string): FileStoreParams | null {
        if (!FileStoreUtil.isFileStoreUrl(value)) {
            return null;
        }

        try {
            const params = new URLSearchParams(value);
            const objectType = params.get("object_type");
            const objectId = params.get("object_id");
            const fileName = params.get("file_name");
            const atrbId = params.get("atrb_id");
            const fileKey = params.get("file_key");

            if (!objectType || !objectId || !fileName) {
                console.warn("FileStore URL missing required parameters:", value);
                return null;
            }

            return {
                objectType: Number(objectType),
                objectId: Number(objectId),
                fileName: fileName,
                atrbId: atrbId ? Number(atrbId) : undefined,
                fileKey: fileKey ?? undefined,
            };
        } catch (error) {
            console.error("Error parsing FileStore URL:", error);
            return null;
        }
    }

    /**
     * Builds the full download URL for a FileStore file
     * @param params FileStore parameters
     * @param display Optional display mode ('inline' or 'attachment')
     * @returns Full URL to download/display the file
     */
    static buildDownloadUrl(params: FileStoreParams, display: "inline" | "attachment" = "attachment"): string {
        const queryParams = new URLSearchParams({
            file_name: params.fileName,
            object_type: params.objectType.toString(),
            object_id: params.objectId.toString(),
            display: display,
        });
        if (params.fileKey) {
            queryParams.set("file_key", params.fileKey);
        }
        return `${FileStoreUtil.baseUrl}/fs/download/download_file.json?${queryParams.toString()}`;
    }

    /**
     * Builds a stored value string for file_key–based files (e.g. form submission attachments).
     * @param fileKey e.g. "fs&" + fieldDefinitionId + "&" + submissionId
     * @param fileName uploaded file name
     */
    static buildFileStoreValue(fileKey: string, fileName: string): string {
        const params = new URLSearchParams({
            object_type: "-1",
            object_id: "-1",
            file_key: fileKey,
            file_name: fileName,
        });
        return params.toString();
    }

    /**
     * Convenience method to parse and build download URL in one step
     * @param value The custom attribute value
     * @param display Optional display mode
     * @returns Full download URL or null if invalid
     */
    static getDownloadUrl(value: string, display: "inline" | "attachment" = "attachment"): string | null {
        const params = FileStoreUtil.parseFileStoreUrl(value);
        if (!params) {
            return null;
        }
        return FileStoreUtil.buildDownloadUrl(params, display);
    }

    /**
     * Gets an inline image URL suitable for <img> src attribute
     * @param value The custom attribute value
     * @returns Image URL or null if invalid
     */
    static getImageUrl(value: string): string | null {
        return FileStoreUtil.getDownloadUrl(value, "inline");
    }

    /**
     * Gets the file extension from a FileStore URL
     * @param value The custom attribute value
     * @returns File extension (lowercase) or empty string
     */
    static getFileExtension(value: string): string {
        const params = FileStoreUtil.parseFileStoreUrl(value);
        if (!params || !params.fileName) {
            return "";
        }

        const parts = params.fileName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    /** Extension from a bare file name (e.g. pool list `itemName`), lowercase. */
    static getExtensionFromFileName(fileName: string): string {
        if (!fileName?.trim() || !fileName.includes(".")) {
            return "";
        }
        const parts = fileName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    private static isImageExtension(ext: string): boolean {
        return FILESTORE_IMAGE_EXTENSIONS.includes(ext);
    }

    /**
     * Checks if a FileStore URL points to an image file
     * @param value The custom attribute value
     * @returns true if the file is an image
     */
    static isImage(value: string): boolean {
        return FileStoreUtil.isImageExtension(FileStoreUtil.getFileExtension(value));
    }

    /** True when the file name looks like a raster/vector image (pool picker, etc.). */
    static isImageFileName(fileName: string): boolean {
        return FileStoreUtil.isImageExtension(FileStoreUtil.getExtensionFromFileName(fileName));
    }
}
